import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SelectionToolbar from "./SelectionToolbar";
import {
  COMMENTS_UI_ATTR,
  useEditorComments,
} from "./services/documentCommentsBridge";
import {
  COMMENT_ANCHOR_META,
  COMMENT_BODY_LOAD_META,
  COMMENT_HIGHLIGHT_ATTR,
  commentThreadIdsIn,
  endedCommentDraft,
} from "./services/commentMark";
import { usePlainDocument } from "./services/editorSurface";
import { EditorContent, useEditor } from "@tiptap/react";
import { EditorGround } from "./EditorGround";
import { EditorView } from "@tiptap/pm/view";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

import editorExtensions from "./services/editorExtensions";
import serializeEditorHtml from "./services/serializeEditorHtml";
import { saveWorkspaceContent } from "../FpAppV4/services/saveWorkspaceContent";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

import LeftBar from "../FpFundBrain/Chat2/LeftBar";
import EditorTableOfContent from "./EditorTableOfContent";
import TableContextMenu from "./TableContextMenu";
import { useWorkspaceContext } from "./services/WorkspaceContext";
import "./../FpAppV4/tiptap-editor.css";
import "./../FundBrainTable/scrollbar.css";

/**
 * Preprocesses HTML content to decode HTML-encoded <br> tags within table cells.
 * This fixes the issue where &lt;br&gt; appears as literal text instead of line breaks.
 */
const preprocessEditorContent = (content: string | null): string => {
  if (!content) return "";

  // Decode &lt;br&gt; and &lt;br/&gt; to <br> within table cells
  // This handles both lowercase and uppercase encoded br tags
  // Match table cells (td or th) including self-closing tags, and replace encoded br tags inside them
  return content.replace(
    /(<t[dh](?:\s[^>]*)?>)([\s\S]*?)(<\/t[dh]>)/gi,
    (match, openTag, cellContent, closeTag) => {
      // Decode br tags within the cell content (handles both &lt;br&gt; and &lt;br/&gt;)
      const decodedContent = cellContent.replace(/&lt;br\s*\/?&gt;/gi, "<br>");
      return openTag + decodedContent + closeTag;
    }
  );
};

/** Whether the reader is writing in the document's comments (#13194): the
 *  editor then does not take the focus as it mounts or becomes editable,
 *  since opening the comments can mount the pane's editor afresh while the
 *  composer already has the cursor. Every other surface keeps the editor's
 *  mount focus as it was. */
const writingAComment = (): boolean =>
  document.activeElement instanceof HTMLElement &&
  document.activeElement.closest(`[${COMMENTS_UI_ATTR}]`) !== null;

interface EditorMainProps {
  editorContent: string | null;
  isLeftBarOpen: boolean;
  setIsLeftBarOpen: (open: boolean) => void;
  hideLeftSidebar?: boolean;
  showTableOfContents?: boolean;
  setShowTableOfContents?: (show: boolean) => void;
  readOnly?: boolean;
  /** No page ground and no sheet: the host pane is the card (#12481). */
  flatGround?: boolean;
}

const EditorMain = ({
  editorContent,
  isLeftBarOpen,
  setIsLeftBarOpen,
  hideLeftSidebar = false,
  showTableOfContents = false,
  setShowTableOfContents,
  readOnly = false,
  flatGround = false,
}: EditorMainProps) => {
  const fileViewerIsOpen = useSelector(
    (state: RootState) => state.fileViewerData.isOpen
  );
  const { workspaceId: contextWorkspaceId } = useWorkspaceContext();
  const reduxWorkspaceId = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );
  const workspaceId = contextWorkspaceId || reduxWorkspaceId;
  const canEdit = useSelector(
    (state: RootState) => state.fpAppV4Data.isEditable
  );
  // A read-only snapshot (#11835) is never editable, whatever the
  // workspace permission says.
  const isEditable = canEdit && !readOnly;
  const plainDocument = usePlainDocument();

  const editorWrapperRef = useRef<HTMLDivElement>(null!);
  // The scroll container as state too: the selection toolbar positions
  // itself inside it, and must re-render once it exists.
  const [wrapperEl, setWrapperEl] = useState<HTMLDivElement | null>(null);
  const attachWrapper = useCallback((el: HTMLDivElement | null) => {
    if (el) editorWrapperRef.current = el;
    setWrapperEl(el);
  }, []);
  // The document's comments host (#13194), for the live document only.
  const commentsBridge = useEditorComments();
  const comments = readOnly ? null : commentsBridge;
  const hasInitialContentBeenSet = useRef(false);
  const firstRealUserUpdate = useRef(false);
  const hasLocalEdits = useRef(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const editor = useEditor({
    // tiptap v3 flipped this default to false. TableContextMenu below reads
    // the editor during render (isActive decides whether it opens; seven
    // can() calls decide which items are disabled) and EditorTableOfContent
    // re-reads the document on every transaction, so both would freeze on
    // their mount-time state with nothing thrown or logged (#11904).
    shouldRerenderOnTransaction: true,
    extensions: editorExtensions,
    content: preprocessEditorContent(editorContent),
    editable: isEditable,
    onBeforeCreate: ({ editor }) => {
      // Read by the placeholder extension (a static module with no React
      // context): plain Documents must not advertise the "/" prompt.
      editor.storage.fpPlainDocument = plainDocument;
    },
    onCreate: ({ editor }) => {
      if (writingAComment()) {
        editor.commands.setTextSelection(1);
      } else {
        editor.chain().focus().setTextSelection(1).run();
      }

      // Register plugin for adding heading IDs after editor is fully created
      editor.registerPlugin(
        new Plugin({
          key: new PluginKey("addHeadingIds"),
          appendTransaction: (transactions, oldState, newState) => {
            const tr = newState.tr;
            let modified = false;
            newState.doc.descendants((node, pos) => {
              if (node.type.name === "heading") {
                const id = `heading-${node.attrs.level}-${pos}`;
                if (node.attrs.id !== id) {
                  tr.setNodeMarkup(pos, undefined, { ...node.attrs, id });
                  modified = true;
                }
              }
            });
            return modified ? tr : null;
          },
        })
      );
    },
    onUpdate: ({ editor, transaction }) => {
      // A snapshot never writes back: the live document is what autosave
      // tracks, and this instance is not it.
      if (readOnly) return;
      const anchor = transaction.getMeta(COMMENT_ANCHOR_META) as
        | string
        | undefined;
      // A colleague's thread put back on its quote (#13194): no save of its
      // own; the next save of the reader's edits carries it.
      if (anchor === "reanchor") return;
      // A new thread's mark is a real edit even as the first change after
      // the load, which the skip below would swallow: saved, and counted
      // as a local edit so a refetch before the save lands cannot drop it.
      if (anchor === "new") firstRealUserUpdate.current = true;
      if (!firstRealUserUpdate.current) {
        firstRealUserUpdate.current = true;
        return;
      }
      hasLocalEdits.current = true;
      saveWorkspaceContent({ content: editor.getHTML(), workspaceId });
      if (isLeftBarOpen) {
        setIsLeftBarOpen(false);
      }
    },
    editorProps: {
      attributes: {
        class:
          "outline-none preserve-default-styles text-[14px] focus:outline-none min-h-[900px] pt-[20px] pb-[20px] tiptap-editor text-[#27272A] px-4",
      },
      handleScrollToSelection: (view: EditorView) => false,
    },
    onFocus: ({ editor }) => {
      editor.view.dom.classList.add("editor-focused");
    },
    onBlur: ({ editor }) => {
      editor.view.dom.classList.remove("editor-focused");
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditable);
      if (isEditable && !writingAComment()) {
        editor.commands.focus("start");
      }
    }
  }, [editor, isEditable]);

  // Reset local edits flag when switching workspaces
  useEffect(() => {
    hasLocalEdits.current = false;
    hasInitialContentBeenSet.current = false;
    firstRealUserUpdate.current = false;
  }, [workspaceId]);

  // Update editor content when editorContent prop changes (e.g., switching workspaces)
  // This ensures that HTML-encoded br tags are decoded when content is loaded
  useEffect(() => {
    if (editor && editorContent !== null) {
      // Skip overwriting the editor if the user has made local edits.
      // A refetchOnWindowFocus can return stale content from the server before
      // the debounced save completes, which would wipe out unsaved changes
      // (e.g., a newly added prompt box).
      if (hasLocalEdits.current) {
        return;
      }
      const preprocessedContent = preprocessEditorContent(editorContent);
      // emitUpdate: false so loading a report does not look like the user
      // editing it, which onUpdate would autosave back to the server. v2 took
      // the flag positionally; v3 takes an options object and ignores a bare
      // `false`, defaulting emitUpdate to true (#11904).
      // This is safe because editorContent changing usually means loading a different workspace
      // Marked as a body load: the one change after which a comment draft's
      // quote is looked for again (a reader's own select-all and typing
      // replaces the document the same way and is an edit).
      editor
        .chain()
        .setMeta(COMMENT_BODY_LOAD_META, true)
        .setContent(preprocessedContent, { emitUpdate: false })
        .run();

      if (!hasInitialContentBeenSet.current) {
        hasInitialContentBeenSet.current = true;
        requestAnimationFrame(() => {
          if (editorWrapperRef.current) {
            editorWrapperRef.current.scrollTop = 0;
          }
        });
      }
    }
  }, [editor, editorContent]);

  // Keep the storage flag in step with the context: the editor instance is
  // created once (useEditor with default deps), so a same-workspace
  // cross-surface navigation would otherwise leave a stale create-time
  // snapshot — the exact mount-time defect MenuBar just shed (#11548 r16).
  useEffect(() => {
    if (editor) {
      editor.storage.fpPlainDocument = plainDocument;
    }
  }, [editor, plainDocument]);

  // Document comments (#13194): hand the host the editor, draw the open
  // threads it names, report which threads the document carries, and pass
  // a click on a highlight up.
  const onCommentsEditor = comments?.onEditor;
  useEffect(() => {
    if (!editor || !onCommentsEditor) return;
    onCommentsEditor(editor);
    return () => onCommentsEditor(null);
  }, [editor, onCommentsEditor]);

  const openThreadKey = comments ? comments.openThreadIds.join(",") : "";
  const activeThreadId = comments?.activeThreadId ?? null;
  // The quote of the comment being written, drawn until it is posted or
  // cancelled. Handed over as the draft object itself: the editor keeps the
  // range its edits moved for as long as the object is the same.
  const draft = comments?.draft ?? null;
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setCommentHighlights({
      openThreadIds: openThreadKey ? openThreadKey.split(",") : [],
      activeThreadId,
      draft,
    });
  }, [editor, openThreadKey, activeThreadId, draft]);

  // An edit that ended the quote of the comment being written is reported
  // on every transaction until the draft marked ended comes back to the
  // editor; the host writes it once (it ignores a draft that is no longer
  // its own).
  const onDraftEnded = comments?.onDraftEnded;
  useEffect(() => {
    if (!editor || !onDraftEnded) return;
    const onTransaction = (): void => {
      const ended = endedCommentDraft(editor.state);
      if (ended) onDraftEnded(ended);
    };
    editor.on("transaction", onTransaction);
    return () => {
      editor.off("transaction", onTransaction);
    };
  }, [editor, onDraftEnded]);

  const onThreadIdsInDocument = comments?.onThreadIdsInDocument;
  useEffect(() => {
    if (!editor || !onThreadIdsInDocument) return;
    onThreadIdsInDocument(commentThreadIdsIn(editor.state.doc));
    const onTransaction = ({
      transaction,
    }: {
      transaction: { docChanged: boolean };
    }) => {
      if (transaction.docChanged)
        onThreadIdsInDocument(commentThreadIdsIn(editor.state.doc));
    };
    editor.on("transaction", onTransaction);
    return () => {
      editor.off("transaction", onTransaction);
    };
  }, [editor, onThreadIdsInDocument]);

  const onHighlightClick = comments?.onHighlightClick;
  useEffect(() => {
    if (!wrapperEl || !onHighlightClick) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const highlight = target?.closest<HTMLElement>(
        `[${COMMENT_HIGHLIGHT_ATTR}]`
      );
      const threadId = highlight?.getAttribute(COMMENT_HIGHLIGHT_ATTR);
      if (highlight && threadId) onHighlightClick(threadId, highlight);
    };
    wrapperEl.addEventListener("click", onClick);
    return () => wrapperEl.removeEventListener("click", onClick);
  }, [wrapperEl, onHighlightClick]);

  useMemo(() => {
    // The formatting toolbar finds the live editor here; a read-only
    // snapshot must not take its place.
    if (editor && !readOnly) (window as any).fp = { editor };
  }, [editor, readOnly]);

  const updateQIDOfQueryInputs = (
    htmlStr: string
  ): { html: string; qidMapping: Record<string, string> } => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlStr, "text/html");
    const allQidElements = doc.querySelectorAll("[qid]");
    const uniqueQids = new Set<string>();
    allQidElements.forEach((node) => {
      const qid = node.getAttribute("qid");
      if (qid) uniqueQids.add(qid);
    });

    const qidMapping: Record<string, string> = {};
    let resultHtml = doc.body.innerHTML;
    for (const oldQid of uniqueQids) {
      const newQid = uuidv4();
      qidMapping[newQid] = oldQid;
      resultHtml = resultHtml.replaceAll(oldQid, newQid);
    }

    return { html: resultHtml, qidMapping };
  };

  const editorCopyOverride = async (
    e: React.ClipboardEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    let htmlStr = e.clipboardData?.getData("text/html") || "";
    const { html: updatedHtml, qidMapping } = updateQIDOfQueryInputs(htmlStr);

    if (Object.keys(qidMapping).length > 0 && workspaceId) {
      const mapData = JSON.stringify({
        qidMapping,
        sourceWorkspaceId: workspaceId,
      });
      htmlStr =
        updatedHtml +
        `<template data-fp-qid-map='${mapData.replace(/'/g, "&#39;")}'></template>`;
    } else {
      htmlStr = updatedHtml;
    }

    const serializedText = serializeEditorHtml(htmlStr);
    const textBlob = new Blob([serializedText], { type: "text/plain" });
    const htmlBlob = new Blob([htmlStr], { type: "text/html" });
    const standardClipboardWrite = async () => {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": textBlob,
            "text/html": htmlBlob,
          }),
        ]);
        return true;
      } catch {
        return false;
      }
    };
    const permissionBasedWrite = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "clipboard-write" as PermissionName,
        });
        if (
          permissionStatus.state === "granted" ||
          permissionStatus.state === "prompt"
        ) {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/plain": textBlob,
              "text/html": htmlBlob,
            }),
          ]);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };
    const legacyFallback = () => {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = serializedText;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        return successful;
      } catch {
        return false;
      }
    };

    const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
    if (isFirefox) {
      if (!(await permissionBasedWrite())) legacyFallback();
    } else {
      if (!(await standardClipboardWrite())) {
        if (!(await permissionBasedWrite())) legacyFallback();
      }
    }
  };

  // Determine if we should show the left sidebar area
  const shouldShowLeftSidebar =
    !fileViewerIsOpen &&
    !hideLeftSidebar &&
    (isLeftBarOpen || showTableOfContents);

  // Show table of contents even when hideLeftSidebar is true
  const shouldShowTableOfContents = !fileViewerIsOpen && showTableOfContents;

  // Close table of contents when left bar is opened
  useEffect(() => {
    if (isLeftBarOpen && showTableOfContents && setShowTableOfContents) {
      setShowTableOfContents(false);
    }
  }, [isLeftBarOpen, showTableOfContents, setShowTableOfContents]);

  // Add right-click handler for table cells
  useEffect(() => {
    if (!editor || !editorWrapperRef.current) return;

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if the click is on a table cell or header
      const cell = target.closest("td, th");
      if (!cell) {
        return;
      }

      // Check if we're in a table within the editor
      const table = cell.closest("table");
      if (!table || !editorWrapperRef.current?.contains(table)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Try to focus the cell to ensure editor context is correct
      // The menu actions will use editor.chain().focus() which will work correctly
      try {
        const cellElement = cell as HTMLElement;
        // Set cursor position to the cell if possible
        const pos = editor.view.posAtDOM(cellElement, 0);
        if (pos !== null && pos !== undefined && pos >= 0) {
          editor.commands.setTextSelection(pos);
        }
      } catch (error) {
        // If we can't set selection, continue anyway - the menu will still work
        // because the menu actions use editor.chain().focus() which sets the correct context
      }

      setContextMenuPosition({
        x: e.clientX,
        y: e.clientY,
      });
    };

    const editorElement = editorWrapperRef.current;
    editorElement.addEventListener("contextmenu", handleContextMenu);

    return () => {
      editorElement.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [editor]);

  return (
    <div
      className={`h-full w-full flex ${
        // Inside the floating pane the document is white on the card
        // (#12481, Gerry: the page ground read as "the same as the chat").
        flatGround ? "bg-[var(--fp-surface)]" : "bg-[var(--fp-bg)]"
      }`}
    >
      {shouldShowLeftSidebar && (
        <div className="w-[250px] border-r border-gray-200">
          {isLeftBarOpen ? (
            <LeftBar
              showCollapseButton={true}
              closeDrawer={() => setIsLeftBarOpen(false)}
            />
          ) : showTableOfContents ? (
            <EditorTableOfContent
              editor={editor || undefined}
              editorWrapperRef={editorWrapperRef}
              onClose={() => setShowTableOfContents?.(false)}
            />
          ) : null}
        </div>
      )}
      {shouldShowTableOfContents && !shouldShowLeftSidebar && (
        <div className="w-[250px] border-r border-gray-200">
          <EditorTableOfContent
            editor={editor || undefined}
            editorWrapperRef={editorWrapperRef}
            onClose={() => setShowTableOfContents?.(false)}
          />
        </div>
      )}
      <div
        className={clsx(
          "relative h-full overflow-auto",
          "fp-scroll-bare",
          fileViewerIsOpen || (hideLeftSidebar && !showTableOfContents)
            ? "w-[100%]"
            : "w-full"
        )}
        ref={attachWrapper}
        id="editor-wrapper"
        onCopy={editorCopyOverride}
      >
        <EditorGround flat={flatGround}>
          <EditorContent editor={editor} />
        </EditorGround>
        {/* The floating toolbar over a selection (#13194): plain documents
            only, never a read-only snapshot or the report editor. */}
        {editor && isEditable && plainDocument && (
          <SelectionToolbar
            editor={editor}
            container={wrapperEl}
            onComment={comments?.onStartComment}
          />
        )}
      </div>
      <TableContextMenu
        editor={editor}
        position={contextMenuPosition}
        onClose={() => setContextMenuPosition(null)}
      />
    </div>
  );
};

export default EditorMain;
